import { useCallback, useEffect, useRef } from 'react'

import { cn } from '@/utils/style'

interface MfaRecoveryKeyInputProps {
  value: string
  onChange: (value: string) => void
  onComplete?: (value: string) => void
  disabled?: boolean
  hasError?: boolean
  autoFocus?: boolean
}

// Recovery key: 8 alphanumeric chars shown as two groups of 4 (XXXX-XXXX).
const KEY_LENGTH = 8
const GROUP_SIZE = 4

function sanitize(input: string): string {
  return input.replace(/[^a-zA-Z0-9]/g, '').toUpperCase()
}

export function MfaRecoveryKeyInput({
  value,
  onChange,
  onComplete,
  disabled = false,
  hasError = false,
  autoFocus = false,
}: MfaRecoveryKeyInputProps) {
  const inputRefs = useRef<(HTMLInputElement | null)[]>([])

  const chars = Array.from({ length: KEY_LENGTH }, (_, index) => value[index] || '')

  const focusInput = useCallback((index: number) => {
    inputRefs.current[index]?.focus()
  }, [])

  useEffect(() => {
    if (autoFocus) {
      focusInput(0)
    }
  }, [autoFocus, focusInput])

  useEffect(() => {
    if (value === '' && hasError) {
      focusInput(0)
    }
  }, [value, hasError, focusInput])

  const commitValue = (nextValue: string) => {
    onChange(nextValue)
    if (nextValue.length === KEY_LENGTH) {
      onComplete?.(nextValue)
    }
  }

  const handleChange = (index: number, nextChar: string) => {
    const sanitized = sanitize(nextChar).slice(-1)

    if (hasError) {
      commitValue(sanitized)
      focusInput(sanitized ? 1 : 0)
      return
    }

    const nextChars = [...chars]
    nextChars[index] = sanitized
    const nextValue = nextChars.join('').slice(0, KEY_LENGTH)
    commitValue(nextValue)

    if (sanitized && index < KEY_LENGTH - 1) {
      focusInput(index + 1)
    }
  }

  const handleKeyDown = (index: number, key: string) => {
    if (key === 'Backspace' && !chars[index] && index > 0) {
      focusInput(index - 1)
    }
  }

  const handlePaste = (event: React.ClipboardEvent<HTMLInputElement>) => {
    event.preventDefault()
    const pasted = sanitize(event.clipboardData.getData('text')).slice(0, KEY_LENGTH)
    if (!pasted) {
      return
    }
    commitValue(pasted)
    focusInput(Math.min(pasted.length, KEY_LENGTH - 1))
  }

  const renderBox = (index: number) => (
    <input
      key={index}
      ref={(element) => {
        inputRefs.current[index] = element
      }}
      type="text"
      autoComplete="one-time-code"
      maxLength={1}
      value={chars[index]}
      disabled={disabled}
      className={cn(
        'w-10 h-12 rounded-lg border text-center t-sb text-primary uppercase outline-none transition-colors',
        hasError ? 'border-error bg-error/5' : 'border-neutral-200 bg-neutral-50',
        'focus:border-btn-secondary',
        disabled && 'opacity-60 cursor-not-allowed',
      )}
      onChange={(event) => handleChange(index, event.target.value)}
      onKeyDown={(event) => handleKeyDown(index, event.key)}
      onPaste={handlePaste}
    />
  )

  return (
    <div className="flex items-center gap-2">
      {Array.from({ length: GROUP_SIZE }, (_, index) => renderBox(index))}
      <span className="w-2.5 h-[2px] rounded bg-neutral-300 shrink-0" />
      {Array.from({ length: KEY_LENGTH - GROUP_SIZE }, (_, index) => renderBox(GROUP_SIZE + index))}
    </div>
  )
}
